// src/encounter/lib/to-vitals-request.ts
// Build the wire body for saving the 8 pre-encounter vitals (PROD-1245). Flutter saves a
// List<PatientVitals> via POST /api/Patients/CreateVitals or PUT /api/Patients/updateVitals
// (patient_chart_data_source_impl.dart:1058-1114); the body is each vital's toJson
// (patient_vitals_model.dart:107-132). Pure — transport + cache live in use-save-vitals.ts.
//
// Scope: FULL COVERAGE (PROD-1277 closed 2026-08-08, Andrew: "why not all the vitals I can enter
// data in?"). The 8 core vitals plus the extended anthropometrics/screening set Flutter's
// emptyVitalUiList carries — hip, waist, bioage, hearing, LMP, head circumference. Two remain
// non-enterable BY DESIGN, not by omission: BMI and waist-to-hip ratio are SERVER-COMPUTED from
// height/weight and waist/hip (the cells stay read-only, matching Flutter's isComputed), and
// VISUAL ACUITY has its own multi-mode form (distance/near x left/right x sc/cc/ph) which writes
// `extraData` — a single text input cannot express it, so the cell hands off to VisualAcuityForm.
// Validation mirrors Flutter vital_utils.dart:25-105 — BP systolic+diastolic both-or-neither; numeric.
import type { PatientVital } from '@/types/vitals-api';
import { VITAL_CODES } from '@/types/vitals-api';

const LOINC_SYSTEM = 'http://loinc.org';

/** Flutter AppConstants.vitalReviewed — the vital `status` value that marks the set reviewed. */
export const VITAL_REVIEWED_STATUS = 'reviewed';

/** Raw form strings (one per editable vital). BMI + waist-to-hip are computed, never entered;
 *  visual acuity rides its own form (see the scope note above). */
export interface VitalsFormValue {
  temp: string;
  pulse: string;
  systolic: string;
  diastolic: string;
  respRate: string;
  o2Sat: string;
  height: string;
  weight: string;
  // Extended anthropometrics / screening (Flutter emptyVitalUiList parity).
  hipCirc: string;
  waistCirc: string;
  bioage: string;
  hearing: string;
  lmp: string;
  headCirc: string;
  /**
   * Visual acuity's OWN cell values — the 4-slash summary `OS_num/OS_den/OD_num/OD_den` the wire
   * stores in `quantity.value` (same shape the appointment panel's inline editor writes). The
   * DETAIL grid (distance/near x sc/cc/ph) is a separate concern: it writes `extraData` through
   * buildVisualAcuityRequest. Cell = the summary, `+` panel = the detail.
   */
  vaOsNum: string;
  vaOsDen: string;
  vaOdNum: string;
  vaOdDen: string;
}

/** Empty slots ship as this placeholder when the summary is partial (Hearing uses it too). */
const VA_PLACEHOLDER = '—';

/** Join the four acuity parts into the wire's 4-slash summary, or '' when nothing is entered. */
export function joinVisualAcuitySummary(form: VitalsFormValue): string {
  const parts = [form.vaOsNum, form.vaOsDen, form.vaOdNum, form.vaOdDen].map((p) => p.trim());
  if (parts.every((p) => !p)) return '';
  return parts.map((p) => p || VA_PLACEHOLDER).join('/');
}

/** Split a stored 4-slash summary back into its parts (placeholders become empty inputs). */
export function splitVisualAcuitySummary(raw: string | null | undefined): {
  vaOsNum: string;
  vaOsDen: string;
  vaOdNum: string;
  vaOdDen: string;
} {
  const [a = '', b = '', c = '', d = ''] = String(raw ?? '').split('/');
  const clean = (p: string) => (p === VA_PLACEHOLDER ? '' : p.trim());
  return { vaOsNum: clean(a), vaOsDen: clean(b), vaOdNum: clean(c), vaOdDen: clean(d) };
}

interface VitalSpec {
  key: keyof VitalsFormValue;
  loinc: string;
  display: string;
  unit: string;
  /** Non-numeric value (hearing "L/R", LMP date) — skipped by the numeric validator. */
  text?: boolean;
}

// Order mirrors the DS VitalsRow layout.
const VITAL_SPECS: VitalSpec[] = [
  { key: 'temp', loinc: VITAL_CODES.TEMPERATURE, display: 'Temperature', unit: 'Cel' },
  { key: 'pulse', loinc: VITAL_CODES.HEART_RATE, display: 'Heart Rate', unit: '/min' },
  { key: 'systolic', loinc: VITAL_CODES.SYSTOLIC, display: 'Systolic Blood Pressure', unit: 'mm[Hg]' },
  { key: 'diastolic', loinc: VITAL_CODES.DIASTOLIC, display: 'Diastolic Blood Pressure', unit: 'mm[Hg]' },
  { key: 'respRate', loinc: VITAL_CODES.RESP_RATE, display: 'Respiratory Rate', unit: '/min' },
  { key: 'o2Sat', loinc: VITAL_CODES.O2_SAT, display: 'Oxygen Saturation', unit: '%' },
  { key: 'height', loinc: VITAL_CODES.HEIGHT, display: 'Body Height', unit: 'cm' },
  { key: 'weight', loinc: VITAL_CODES.WEIGHT, display: 'Body Weight', unit: 'kg' },
  { key: 'hipCirc', loinc: VITAL_CODES.HIP, display: 'Hip Circumference', unit: 'cm' },
  { key: 'waistCirc', loinc: VITAL_CODES.WAIST, display: 'Waist Circumference', unit: 'cm' },
  { key: 'bioage', loinc: VITAL_CODES.BIOAGE, display: 'Bioage', unit: '' },
  // Free text, not numeric: Flutter stores hearing as "<Left>/<Right>" and LMP as a date string.
  { key: 'hearing', loinc: VITAL_CODES.HEARING, display: 'Hearing Test', unit: '', text: true },
  { key: 'lmp', loinc: VITAL_CODES.LMP, display: 'Last Menstrual Period', unit: '', text: true },
  // No unit, as Flutter saves it (ui_data.dart, PROD-3185); readers take a missing unit as cm.
  { key: 'headCirc', loinc: VITAL_CODES.HCM, display: 'Head Circumference', unit: '' },
];

/** The LMP row's booleans — REAL wire fields (`PatientVital.isMenstrual` / `isBreastFeeding`, vitals-api.ts). */
export interface LmpFlags {
  isMenstrual?: boolean;
  isBreastFeeding?: boolean;
}

/** The one place both save paths write the LMP flags. */
function setLmpFlags(reading: PatientVital, flags: LmpFlags): void {
  reading.isMenstrual = flags.isMenstrual ?? null;
  reading.isBreastFeeding = flags.isBreastFeeding ?? null;
}

export interface VitalsRequestContext {
  patientId: string;
  encounterId: string;
  /** Current readings — drives create-vs-update + the identifire round-trip on update. */
  existing?: PatientVital[] | null;
  /** ISO timestamp for effectiveDateTime (injected so the builder stays pure/testable). */
  now: string;
}

export interface VitalsRequest {
  mode: 'create' | 'update';
  vitals: PatientVital[];
}

export type VitalsRequestResult = VitalsRequest | { error: string };

function notDeleted(items: PatientVital[] | null | undefined): PatientVital[] {
  return (items ?? []).filter((v) => !v.isDeleted);
}

/** Existing reading for a LOINC (to round-trip its identifire on update). */
function existingFor(existing: PatientVital[] | null | undefined, loinc: string): PatientVital | undefined {
  return notDeleted(existing).find((v) => v.code?.code === loinc);
}

/** One reading for a code, round-tripping the current reading's `identifire` (exported for PROD-3183). */
export function makeReading(spec: { loinc: string; display: string; unit: string }, value: string, ctx: VitalsRequestContext): PatientVital {
  const prev = existingFor(ctx.existing, spec.loinc);
  return {
    // `identifire` is the VERBATIM wire field — the .NET binder is spelling-sensitive (see
    // vitals-api.ts). Intentional misspelling, do NOT "fix" it. Round-tripped on update so the
    // backend updates the existing reading in place rather than orphaning it.
    identifire: prev?.identifire ?? null,
    status: 'final',
    code: { code: spec.loinc, display: spec.display, system: LOINC_SYSTEM },
    quantity: { value, unit: spec.unit, code: spec.loinc, system: 'http://unitsofmeasure.org' },
    subject: ctx.patientId,
    encounter: ctx.encounterId,
    category: 'vital-signs',
    effectiveDateTime: ctx.now,
    isDeleted: false,
  };
}

/** Compute BMI from height(cm) + weight(kg), 1 dp. Returns '' when either is missing/invalid. */
export function computeBmi(heightCm: string, weightKg: string): string {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!h || !w || Number.isNaN(h) || Number.isNaN(w)) return '';
  const m = h / 100;
  const bmi = w / (m * m);
  if (!Number.isFinite(bmi)) return '';
  return bmi.toFixed(1);
}

/**
 * Validate the raw form (mirrors Flutter vital_utils.dart). Returns an error string, or null when
 * valid. Shared by the dialog (inline UX) and buildVitalsRequest (source of truth) so the rules
 * live in one place: BP both-or-neither · each entered field numeric · at least one field entered.
 */
export function validateVitalsForm(form: VitalsFormValue): string | null {
  const sys = form.systolic.trim();
  const dia = form.diastolic.trim();
  if ((sys && !dia) || (!sys && dia)) {
    return 'Enter both systolic and diastolic blood pressure.';
  }
  let any = false;
  for (const spec of VITAL_SPECS) {
    const raw = form[spec.key].trim();
    if (!raw) continue;
    any = true;
    // Strict finite-decimal only — reject Infinity / hex (0x10) / scientific (1e5) which Number()
    // would silently accept and ship as a garbage reading (Codex). Vitals are non-negative decimals.
    if (spec.text) continue; // free text (hearing "L/R", LMP date) - not a decimal
    if (!/^\d+(\.\d+)?$/.test(raw)) return `${spec.display} must be a number.`;
  }
  if (!any && !joinVisualAcuitySummary(form)) return 'Enter at least one vital.';
  return null;
}

/**
 * Resolve the form into a CreateVitals/UpdateVitals body. Returns `{ error }` on invalid input,
 * else `{ mode, vitals }`. Only non-empty fields are sent; BMI is computed + appended when
 * height+weight are present.
 */
export function buildVitalsRequest(
  form: VitalsFormValue,
  ctx: VitalsRequestContext,
  /** PROD-3130: the add-vitals form now takes LMP, so it writes the LMP row's flags as the inline grid's
   *  `buildVitalsRequestFromCodes` does. Absent: the LMP row carries none, as before. */
  lmpFlags?: LmpFlags,
): VitalsRequestResult {
  const error = validateVitalsForm(form);
  if (error) return { error };

  const vitals: PatientVital[] = [];
  // Visual acuity is COMPOSITE — four inputs collapse into one reading — so it can't ride the
  // one-key-per-spec sweep below and is appended after it.
  const vaSummary = joinVisualAcuitySummary(form);
  for (const spec of VITAL_SPECS) {
    const raw = form[spec.key].trim();
    if (!raw) continue;
    const reading = makeReading(spec, raw, ctx);
    if (spec.key === 'lmp' && lmpFlags) setLmpFlags(reading, lmpFlags);
    vitals.push(reading);
  }
  // Composite: the acuity cell's four inputs become ONE reading carrying the 4-slash summary.
  // extraData (the detail grid) is written separately by buildVisualAcuityRequest and is left
  // untouched here, so editing the summary never clobbers a recorded distance/near grid.
  if (vaSummary) {
    vitals.push(
      makeReading(
        { loinc: VITAL_CODES.VISUAL, display: 'Visual Acuity', unit: '' },
        vaSummary,
        ctx,
      ),
    );
  }

  // Computed BMI (read-only field) — append when both height + weight were entered.
  const bmi = computeBmi(form.height, form.weight);
  if (bmi) {
    vitals.push(makeReading({ loinc: VITAL_CODES.BMI, display: 'Body Mass Index', unit: 'kg/m2' }, bmi, ctx));
  }

  const mode: 'create' | 'update' = notDeleted(ctx.existing).length > 0 ? 'update' : 'create';
  return { mode, vitals };
}

/**
 * True when the recorded vitals are marked reviewed (PROD-1536). Flutter derives this from
 * `vitals.first.status == "reviewed"` (patient_encounter_bloc.dart:756); we treat the set as
 * reviewed when ANY non-deleted reading carries the reviewed status.
 */
export function isVitalsReviewed(existing: PatientVital[] | null | undefined): boolean {
  return notDeleted(existing).some((v) => (v.status ?? '').toLowerCase() === VITAL_REVIEWED_STATUS);
}

/**
 * Build the "mark reviewed" body (PROD-1536): re-emit the CURRENT readings unchanged except
 * status → "reviewed" (Flutter stamps the reviewed status on the vitals via the same save path —
 * patient_encounter_bloc.dart:2860). Round-trips each reading's identifire so the backend updates
 * in place. Returns `{ error }` when there are no readings to mark.
 */
export function buildMarkReviewedRequest(existing: PatientVital[] | null | undefined): VitalsRequestResult {
  const current = notDeleted(existing);
  if (current.length === 0) return { error: 'No vitals to review.' };
  return {
    mode: 'update',
    vitals: current.map((v) => ({ ...v, status: VITAL_REVIEWED_STATUS })),
  };
}

/** Read the current numeric values (by LOINC) from existing readings → form pre-fill. */
export function vitalsToFormValue(existing: PatientVital[] | null | undefined): VitalsFormValue {
  const byCode = (loinc: string) => notDeleted(existing).find((v) => v.code?.code === loinc)?.quantity?.value ?? '';
  return {
    temp: byCode(VITAL_CODES.TEMPERATURE),
    pulse: byCode(VITAL_CODES.HEART_RATE),
    systolic: byCode(VITAL_CODES.SYSTOLIC),
    diastolic: byCode(VITAL_CODES.DIASTOLIC),
    respRate: byCode(VITAL_CODES.RESP_RATE),
    o2Sat: byCode(VITAL_CODES.O2_SAT),
    height: byCode(VITAL_CODES.HEIGHT),
    weight: byCode(VITAL_CODES.WEIGHT),
    hipCirc: byCode(VITAL_CODES.HIP),
    waistCirc: byCode(VITAL_CODES.WAIST),
    bioage: byCode(VITAL_CODES.BIOAGE),
    hearing: byCode(VITAL_CODES.HEARING),
    lmp: byCode(VITAL_CODES.LMP),
    headCirc: byCode(VITAL_CODES.HCM),
    ...splitVisualAcuitySummary(byCode(VITAL_CODES.VISUAL)),
  };
}

// ── code-keyed variant ────────────────────────────────────────────────────────────────────────
// The pre-encounter grid reuses the appointment panel's `VitalCell`, whose working copy is keyed
// by LOINC rather than by the eight named fields above. Its bespoke editors emit ALREADY-COMBINED
// values — hearing as "<Left>/<Right>", visual acuity as the 4-slash OS/OD summary — so each code
// maps to exactly one reading and no composite assembly is needed here.

/** Codes whose value is free text, not a decimal (the validator must not reject them). */
const TEXT_CODES = new Set<string>([
  VITAL_CODES.HEARING,
  VITAL_CODES.VISUAL,
  VITAL_CODES.LMP,
]);

/** Codes the BACKEND computes — never sent, even if a stale form value lingers. */
const COMPUTED_CODES = new Set<string>([VITAL_CODES.BMI, VITAL_CODES.WAIST_TO_HIP_RATIO]);

/** Display names for the validator's messages, by code. */
const DISPLAY_BY_CODE = new Map<string, string>(VITAL_SPECS.map((v) => [v.loinc, v.display]));

/**
 * Validate a code-keyed form. Same rules as `validateVitalsForm`: BP both-or-neither, every
 * entered numeric field a plain decimal, at least one value present. Fires on Save only.
 */
export function validateVitalsCodeForm(form: Record<string, string>): string | null {
  const sys = (form[VITAL_CODES.SYSTOLIC] ?? '').trim();
  const dia = (form[VITAL_CODES.DIASTOLIC] ?? '').trim();
  if ((sys && !dia) || (!sys && dia)) return 'Enter both systolic and diastolic blood pressure.';
  let any = false;
  for (const [code, raw] of Object.entries(form)) {
    const value = (raw ?? '').trim();
    if (!value || COMPUTED_CODES.has(code)) continue;
    any = true;
    if (TEXT_CODES.has(code)) continue;
    if (!/^\d+(\.\d+)?$/.test(value)) {
      return `${DISPLAY_BY_CODE.get(code) ?? 'This vital'} must be a number.`;
    }
  }
  if (!any) return 'Enter at least one vital.';
  return null;
}

/** Resolve a code-keyed form into a CreateVitals/UpdateVitals body. */
export function buildVitalsRequestFromCodes(
  form: Record<string, string>,
  ctx: VitalsRequestContext,
  /** LMP-row booleans.  and  are REAL wire fields
   *  (vitals-api.ts:60-61) and were previously never written by this path. */
  lmpFlags?: LmpFlags,
): VitalsRequestResult {
  const error = validateVitalsCodeForm(form);
  if (error) return { error };

  const vitals: PatientVital[] = [];
  for (const [code, raw] of Object.entries(form)) {
    const value = (raw ?? '').trim();
    if (!value || COMPUTED_CODES.has(code)) continue;
    const reading = makeReading(
      { loinc: code, display: DISPLAY_BY_CODE.get(code) ?? code, unit: unitForCode(code) },
      value,
      ctx,
    );
    if (code === VITAL_CODES.LMP && lmpFlags) {
      setLmpFlags(reading, lmpFlags);
    }
    vitals.push(reading);
  }
  // BMI still rides along when height+weight are present — the server recomputes it, but sending
  // it keeps the create body identical to the named-field path (and to Flutter's).
  const bmi = computeBmi(form[VITAL_CODES.HEIGHT] ?? '', form[VITAL_CODES.WEIGHT] ?? '');
  if (bmi) {
    vitals.push(makeReading({ loinc: VITAL_CODES.BMI, display: 'Body Mass Index', unit: 'kg/m2' }, bmi, ctx));
  }
  const mode: 'create' | 'update' = notDeleted(ctx.existing).some((v) => v.identifire) ? 'update' : 'create';
  return { mode, vitals };
}

function unitForCode(code: string): string {
  return VITAL_SPECS.find((v) => v.loinc === code)?.unit ?? '';
}
